import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { getMediaUrl, type TSubmissionFeedback } from '@onesource/services';
import { DateTime } from 'luxon';
import { FC } from 'react';

import { AnswerList } from './answer-list';
import { getFeedbackItems, wasFeedbackUpdated } from './feedback-answer-text';
import { FEEDBACK_TINT } from './feedback-palette';

const formatDay = (value: string | null | undefined) =>
  value ? DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_MED) : '';

/**
 * The teacher's feedback as a note from the teacher: who wrote it and when, then
 * each answered question as a small heading over plain text. Grades print as a
 * compact row per topic. Matches the "Teacher feedback" block of the PDF.
 */
export const FeedbackNote: FC<{ feedback: TSubmissionFeedback }> = ({ feedback }) => {
  const items = getFeedbackItems(feedback);
  const { teacher } = feedback;

  return (
    <Box
      sx={{
        bgcolor: FEEDBACK_TINT.bg,
        borderLeft: `3px solid ${FEEDBACK_TINT.bar}`,
        borderRadius: '10px',
        px: { xs: 2, sm: 2.5 },
        py: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: items.length > 0 ? 2 : 0 }}>
        <Avatar
          src={teacher.avatar ? getMediaUrl(teacher.avatar) : undefined}
          sx={{ width: 32, height: 32, fontSize: '0.875rem', bgcolor: FEEDBACK_TINT.bar }}
        >
          {teacher.name.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
            Feedback from <strong>{teacher.name}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDay(feedback.submittedAt)}
            {wasFeedbackUpdated(feedback) ? ` · Updated ${formatDay(feedback.updatedAt)}` : ''}
          </Typography>
        </Box>
      </Box>

      <AnswerList items={items} accentColor={FEEDBACK_TINT.ink} gradeBorderColor={FEEDBACK_TINT.border} />
    </Box>
  );
};
